<cipo-dialog-header
    testId="module-config-field-dialog-header"
    closeTestId="module-config-field-action-close"
    [customCloseMode]="true"
    (closeClick)="tryClose()">
    @let heading = title() | translate: { type: (typeLabel() ?? "" | translate) };
    <div class="cipo--flex--row cipo--align--center cipo--gap--md cipo--width--full cipo--min-width--0">
        <h5 class="m-0 cipo--min-width--0 cipo--text--ellipsed" [attr.title]="heading">{{ heading }}</h5>
        <div class="cipo--flex--row cipo--flex--none cipo--align--center cipo--gap--xs cipo--display--none--phone">
            @if (displayBadge()) {
                <span
                    class="mat-body-2 cipo-bg--primary-100 cipo-text--black-800 p-x-sm p-y-xs cipo--border-radius--xs">
                    {{ displayBadge() | translate }}
                </span>
            }
            @for (badge of detailBadges(); track badge) {
                <span class="mat-body-2 cipo-bg--black-100 cipo-text--black-800 p-x-sm p-y-xs cipo--border-radius--xs">
                    {{ badge | translate }}
                </span>
            }
            @if (isTableField()) {
                <span class="mat-body-2 cipo-bg--accent-50 cipo-text--accent-900 p-x-sm p-y-xs cipo--border-radius--xs">
                    {{ "modules.fieldConfig.tableField" | translate }}
                </span>
            }
        </div>
    </div>
</cipo-dialog-header>

<mat-dialog-content>
    @if (loading()) {
        <div
            class="p-y"
            fxLayout="column"
            fxLayoutGap="16px"
            role="status"
            [attr.aria-busy]="true"
            [attr.aria-label]="'a11y.moduleConfig.loadingField' | translate">
            @for (row of [1, 2, 3, 4]; track row) {
                <ngx-skeleton-loader count="1" [theme]="{ width: '100%', height: '56px', margin: '0' }" />
            }
        </div>
    } @else if (unsupported()) {
        <div class="p-y">
            <p class="mat-body-2 cipo-text--warn-900">{{ "modules.fieldConfig.unsupportedType" | translate }}</p>
        </div>
    } @else if (failed()) {
        <div class="p-y">
            <cipo-load-error
                message="modules.fieldConfig.loadError"
                testId="module-config-field-retry"
                (retry)="load()" />
        </div>
    } @else {
        <div class="p-y" fxLayout="column" fxLayoutGap="16px" cipoAutofocus>
            <section class="cipo-outlined-field p-a-md" fxLayout="column" fxLayoutGap="12px">
                <h5 class="m-a-0">{{ "modules.fieldConfig.details" | translate }}</h5>
                <div
                    class="cipo--grid cipo--grid--cols-2 cipo--grid--cols-1--phone cipo--gap--lg cipo--row-gap--md cipo--align--start">
                    <cipo-text [control]="form.controls.name" />
                    <cipo-text [control]="form.controls.label" />
                    @if (inlinesDefaultValue()) {
                        <cipo-control [control]="$any(form.controls.defaultValue)" [fieldData]="defaultValueConfig()" />
                    }
                    <div class="cipo--flex--row cipo--flex--wrap cipo--align--center cipo--gap--lg cipo--row-gap--md">
                        <cipo-select
                            class="cipo--flex--1"
                            [control]="form.controls.displayTypeId"
                            [fieldData]="displayTypeFieldData()" />
                        @if (canChooseMultiple()) {
                            <cipo-checkbox [control]="form.controls.hasMultipleValues" />
                        }
                    </div>
                </div>
            </section>

            @if (showsDataSource()) {
                <section class="cipo-outlined-field p-a-md" fxLayout="column" fxLayoutGap="12px">
                    <h5 class="m-a-0">{{ "modules.fieldConfig.dataSourceSection" | translate }}</h5>
                    <div fxLayout.gt-sm="row" fxLayout.lt-md="column" fxLayoutGap="16px">
                        <div fxFlex fxLayout="column" fxLayoutGap="4px">
                            <cipo-select [control]="form.controls.dataSourceId" [fieldData]="dataSourceFieldData()" />
                            @if (selectedDataSource()?.parentName) {
                                <span class="mat-caption cipo-text--warn-900">
                                    {{
                                        "modules.fieldConfig.dataSourceParent"
                                            | translate: { parent: selectedDataSource().parentName }
                                    }}
                                </span>
                            }
                        </div>
                        <div fxFlex>
                            @if (loadingElements()) {
                                <ngx-skeleton-loader
                                    role="status"
                                    [attr.aria-busy]="true"
                                    [attr.aria-label]="'a11y.moduleConfig.loadingListElements' | translate"
                                    [theme]="{ width: '100%', height: '56px', margin: '0' }" />
                            } @else {
                                <cipo-select
                                    [control]="$any(form.controls.defaultValue)"
                                    [fieldData]="defaultValueConfig()" />
                            }
                        </div>
                    </div>
                </section>
            }

            @if (showsWeather()) {
                <section class="cipo-outlined-field p-a-md" fxLayout="column" fxLayoutGap="12px">
                    <h5 class="m-a-0">{{ "modules.fieldConfig.weatherSection" | translate }}</h5>
                    <div fxLayout.gt-sm="row" fxLayout.lt-md="column" fxLayoutGap="16px">
                        <cipo-select
                            fxFlex
                            [control]="form.controls.weatherDateId"
                            [fieldData]="weatherDateFieldData()" />
                        <cipo-select
                            fxFlex
                            [control]="form.controls.weatherLocationId"
                            [fieldData]="weatherLocationFieldData()" />
                    </div>
                </section>
            }

            @if (defaultValueKind() !== "none" && defaultValueKind() !== "list" && !inlinesDefaultValue()) {
                <section class="cipo-outlined-field p-a-md" fxLayout="column" fxLayoutGap="12px">
                    <h5 class="m-a-0">{{ "modules.fieldConfig.defaultValueSection" | translate }}</h5>
                    <div
                        fxLayout.gt-sm="row"
                        fxLayout.lt-md="column"
                        fxLayoutGap="16px"
                        fxLayoutAlign.gt-sm="start center">
                        @if (showsAggregate()) {
                            <cipo-select
                                fxFlex="33"
                                fxFlex.lt-md="100"
                                [control]="form.controls.aggregateId"
                                [fieldData]="aggregateFieldData()" />
                        }

                        @switch (defaultValueKind()) {
                            @case ("richText") {
                                <div fxFlex data-testid="module-config-field-dialog-field-default-value">
                                    <app-froala
                                        [froalaoptions]="richTextOptions"
                                        [label]="'modules.fieldConfig.defaultValue' | translate"
                                        [content]="$any(form.controls.defaultValue.value) ?? ''"
                                        (contentChange)="setRichText($event)" />
                                </div>
                            }
                            @case ("richTextList") {
                                <div fxFlex fxLayout="column" fxLayoutGap="8px">
                                    @for (value of richTextValues(); track $index; let index = $index) {
                                        <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="8px">
                                            <app-froala
                                                fxFlex
                                                [froalaoptions]="richTextOptions"
                                                [label]="
                                                    'modules.fieldConfig.defaultValueNumber'
                                                        | translate: { index: index + 1 }
                                                "
                                                [content]="value"
                                                (contentChange)="setRichTextAt(index, $event)" />
                                            <button
                                                mat-icon-button
                                                [disabled]="richTextValues().length === 1 && !value"
                                                [title]="'actions.delete' | translate"
                                                (click)="removeRichTextValue(index)">
                                                <mat-icon aria-hidden="true">delete</mat-icon>
                                            </button>
                                        </div>
                                    }
                                    <div>
                                        <button mat-stroked-button (click)="addRichTextValue()">
                                            <mat-icon aria-hidden="true">add</mat-icon>
                                            {{ "modules.fieldConfig.addDefaultValue" | translate }}
                                        </button>
                                    </div>
                                </div>
                            }
                            @default {
                                <cipo-control
                                    fxFlex="50"
                                    fxFlex.lt-md="100"
                                    [control]="$any(form.controls.defaultValue)"
                                    [fieldData]="defaultValueConfig()" />
                            }
                        }

                        @if (defaultValueKind() === "date") {
                            <cipo-checkbox
                                [control]="form.controls.currentDateTime"
                                [fieldData]="currentDateFieldData()" />
                        }
                    </div>
                </section>
            }

            @if (showsFormula()) {
                <section class="cipo-outlined-field p-a-md">
                    <cipo-field-formula-views
                        [moduleId]="data.moduleId"
                        [fieldId]="fieldId()"
                        [expression]="expression()"
                        [isDateResult]="isDateType()"
                        [disabled]="screenId() > 0" />
                </section>
            }

            @if (formattingStates().length) {
                <section class="cipo-outlined-field p-a-md" fxLayout="column" fxLayoutGap="12px">
                    <h5 class="m-a-0">{{ "modules.fieldConfig.formatting" | translate }}</h5>
                    <div class="cipo--grid cipo--grid--cols-2 cipo--grid--cols-1--phone cipo--gap--lg">
                        @for (state of formattingStates(); track state.name) {
                            <cipo-control [control]="$any(formattingControl(state))" />
                        }
                    </div>
                </section>
            }

            @if (restrictionStates().length) {
                <section class="cipo-outlined-field p-a-md" fxLayout="column" fxLayoutGap="12px">
                    <h5 class="m-a-0">{{ "modules.fieldConfig.restrictions" | translate }}</h5>
                    <div
                        class="cipo--grid cipo--grid--cols-2 cipo--grid--cols-1--phone cipo--gap--lg cipo--align--center">
                        @for (state of restrictionStates(); track state.name) {
                            @let definition = restrictionDefinition(state);
                            @if (definition.isMimeTypes) {
                                <cipo-select
                                    [control]="$any(restrictionControl(state))"
                                    [fieldData]="mimeTypesFieldData()" />
                            } @else if (definition.isNumberInput || definition.elements.length) {
                                <!-- cipo-number renders no test id of its own. -->
                                <cipo-control
                                    [attr.data-testid]="
                                        definition.isNumberInput ? restrictionControl(state).fieldData?.testId : null
                                    "
                                    [control]="$any(restrictionControl(state))" />
                            } @else if (showsRestrictionCheckbox(state)) {
                                <cipo-checkbox [control]="$any(restrictionControl(state))" />
                            }
                        }
                    </div>
                </section>
            }

            @if (showsFilterSetting()) {
                <section class="cipo-outlined-field p-a-md" fxLayout="column" fxLayoutGap="12px">
                    <h5 class="m-a-0">{{ "modules.fieldConfig.settings" | translate }}</h5>
                    <cipo-checkbox [control]="form.controls.isFilter" />
                </section>
            }

            @if (showsTableSettings()) {
                <section class="cipo-outlined-field p-a-md">
                    @if (fieldId()) {
                        <cipo-table-columns
                            [moduleId]="data.moduleId"
                            [tableId]="fieldId()"
                            [tableTypeId]="typeId()"
                            [tableInUse]="inUse()"
                            [disabled]="saving()"
                            (editingChange)="onTableEditing($event)"
                            (createColumn)="createColumn($event)"
                            (editColumn)="editColumn($event)" />
                    } @else {
                        <h5 class="m-a-0">{{ "modules.fieldConfig.table.title" | translate }}</h5>
                        <p class="mat-body-2 cipo-text--black-500 m-b-0">
                            {{ "modules.fieldConfig.table.saveFirst" | translate }}
                        </p>
                    }
                </section>
            }
        </div>
    }
</mat-dialog-content>

<mat-dialog-actions align="end" [class.cipo-bg--warn-400]="warningContext()">
    @if (warningContext()) {
        <span class="m-r-sm">{{ "general.discardMessage" | translate }}</span>
        <button mat-stroked-button (click)="dismissWarning()">{{ "actions.cancel" | translate }}</button>
        <button
            mat-stroked-button
            color="warn"
            data-testid="module-config-field-action-discard"
            (click)="discardAndClose()">
            {{ "actions.discard" | translate }}
        </button>
        <button
            mat-flat-button
            color="primary"
            data-testid="module-config-field-action-save"
            [disabled]="loading() || formulaLoading() || saving() || tableEditing() || unsupported() || failed()"
            (click)="save()">
            {{ (isEdit() ? "actions.update" : "general.add") | translate }}
        </button>
    } @else {
        @if (!loading() && !failed() && !unsupported()) {
            <div class="cipo--flex--row cipo--flex--1 cipo--flex--wrap cipo--align--center cipo--gap--lg m-r-sm">
                @if (showsFieldVersions()) {
                    <cipo-select
                        class="cipo--flex--1 cipo--max-width--300"
                        [control]="fieldVersion"
                        [fieldData]="fieldVersionFieldData()" />
                }
                @if (showsAddToForm()) {
                    <mat-checkbox
                        data-testid="module-config-field-dialog-add-to-form"
                        [checked]="addToForm()"
                        [disabled]="saving()"
                        (change)="toggleAddToForm($event.checked)">
                        {{ "modules.fieldConfig.addToForm" | translate }}
                    </mat-checkbox>
                }
            </div>
        }
        <button
            mat-button
            data-testid="module-config-field-action-cancel"
            [disabled]="saving() || tableEditing()"
            (click)="tryClose()">
            {{ (isEdit() ? "actions.close" : "actions.cancel") | translate }}
        </button>
        <button
            mat-flat-button
            color="primary"
            data-testid="module-config-field-action-save"
            [disabled]="loading() || formulaLoading() || saving() || tableEditing() || unsupported() || failed()"
            (click)="save()">
            {{ (isEdit() ? "actions.update" : "general.add") | translate }}
        </button>
    }
</mat-dialog-actions>
